import React, { createContext, useContext, useState, useEffect } from 'react';
import { AxiosInstance } from 'axios';
import { withSourceHeader } from '../../utils/httpClient';

interface ApplicationSettingsContextType<T> {
    settings: { featureFlags: T };
    loading: boolean;
    error?: string | null;
}

/**
 * Creates an ApplicationSettingsProvider and useFeatureFlags hook, typed to the app's feature flags.
 *
 * @param defaults - Default feature flag values for this app. Also defines the TypeScript type.
 * @param apiUrl - Base URL for the API (e.g., env.REACT_APP_API_URL).
 * @param httpClient - Axios instance (or compatible) to use for the API call.
 * @param source - Optional default scenario identifier sent as the `x-source` header. Can be overridden per page via the Provider's `source` prop.
 * @returns { Provider, useFeatureFlags, useSettingsLoaded }
 */
export function createApplicationSettings<T extends { [K in keyof T]: boolean }>(
    defaults: T,
    apiUrl: string,
    httpClient: AxiosInstance,
    source?: string
) {
    const Context = createContext<ApplicationSettingsContextType<T> | undefined>(undefined);

    const Provider: React.FC<{ children: React.ReactNode; source?: string }> = ({ children, source: pageSource }) => {
        const [settings, setSettings] = useState<{ featureFlags: T }>({ featureFlags: { ...defaults } });
        const [loading, setLoading] = useState<boolean>(true);
        const [error, setError] = useState<string | null>(null);

        // The page-level source (Provider prop) identifies which page triggered getApplicationSettings.
        const effectiveSource = pageSource ?? source;

        useEffect(() => {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 5000);

            async function loadApplicationSettings() {
                try {
                    setError(null);

                    const res = await httpClient.get<{ featureFlags: Record<string, boolean> }>(
                        `${apiUrl}/api/getApplicationSettings`,
                        {
                            headers: withSourceHeader({ Accept: 'application/json' }, effectiveSource),
                            signal: controller.signal,
                        }
                    );

                    const merged = { ...defaults };
                    const serverFlags = res.data?.featureFlags ?? {};
                    Object.entries(serverFlags).forEach(([key, value]) => {
                        if (key in defaults) {
                            (merged as Record<string, boolean>)[key] = value;
                        }
                    });

                    setSettings({ featureFlags: merged });
                } catch (err) {
                    if (err instanceof Error) {
                        setError(err.message ?? 'Failed to load feature flags');
                    }
                } finally {
                    clearTimeout(timeoutId);
                    setLoading(false);
                }
            }

            loadApplicationSettings();

            return () => {
                controller.abort();
                clearTimeout(timeoutId);
            };
        }, []);

        return (
            <Context.Provider value={{ settings, loading, error }}>
                {children}
            </Context.Provider>
        );
    };

    const useFeatureFlags = (): T => {
        const context = useContext(Context);
        if (context === undefined) {
            throw new Error('useFeatureFlags must be used within an ApplicationSettingsProvider');
        }
        return context.settings.featureFlags;
    };

    const useSettingsLoaded = (): boolean => {
        const context = useContext(Context);
        if (context === undefined) {
            throw new Error('useSettingsLoaded must be used within an ApplicationSettingsProvider');
        }
        return !context.loading;
    };

    return { Provider, useFeatureFlags, useSettingsLoaded };
}
